import {
  ALL_FORMATS,
  BlobSource,
  BufferTarget,
  EncodedAudioPacketSource,
  EncodedPacket,
  EncodedPacketSink,
  EncodedVideoPacketSource,
  Input,
  Mp4OutputFormat,
  Output,
  WebMOutputFormat,
  type AudioCodec,
} from "mediabunny";
import type { VideoEncodingResult, VideoMuxer } from "../../core/export/videoEncoding";
import { AppError } from "../../shared/errors/AppError";

type Container = VideoMuxer["container"];

function mediabunnyCodec(codec: VideoEncodingResult["codec"]) {
  if (codec.startsWith("avc1")) return "avc" as const;
  if (codec.startsWith("vp09")) return "vp9" as const;
  throw new AppError("EXPORT_FAILED", `Unsupported mux codec: ${codec}`);
}

function mimeType(container: Container) {
  return container === "mp4" ? "video/mp4" : "video/webm";
}

function outputFormat(container: Container) {
  return container === "mp4" ? new Mp4OutputFormat() : new WebMOutputFormat();
}

export class MediabunnyVideoMuxer implements VideoMuxer {
  readonly container: Container;
  private readonly audioFile: File | null;

  constructor(container: Container, audioFile: File | null = null) {
    this.container = container;
    this.audioFile = audioFile;
  }

  async mux(result: VideoEncodingResult): Promise<Blob> {
    if (result.samples.length === 0) {
      throw new AppError("EXPORT_FAILED", "The encoder produced no video samples.");
    }

    const format = outputFormat(this.container);
    const target = new BufferTarget();
    const output = new Output({ format, target });
    const videoSource = new EncodedVideoPacketSource(mediabunnyCodec(result.codec));

    output.addVideoTrack(videoSource, { frameRate: result.fps });

    let audioSource: EncodedAudioPacketSource | null = null;
    let audioSink: EncodedPacketSink | null = null;
    let audioDecoderConfig: AudioDecoderConfig | null = null;

    if (this.audioFile) {
      const input = new Input({
        formats: ALL_FORMATS,
        source: new BlobSource(this.audioFile),
      });
      const audioTrack = await input.getPrimaryAudioTrack();
      if (audioTrack) {
        const audioCodec = await audioTrack.getCodec();
        if (!audioCodec) {
          throw new AppError("EXPORT_FAILED", "The source audio codec could not be identified for passthrough.");
        }
        if (!format.getSupportedAudioCodecs().includes(audioCodec)) {
          throw new AppError(
            "EXPORT_FAILED",
            `${audioCodec.toUpperCase()} audio cannot be copied directly into ${this.container.toUpperCase()}. Disable Preserve audio or choose a compatible container.`,
          );
        }

        audioDecoderConfig = await audioTrack.getDecoderConfig();
        if (!audioDecoderConfig) {
          throw new AppError("EXPORT_FAILED", "The source audio decoder configuration is unavailable.");
        }

        audioSource = new EncodedAudioPacketSource(audioCodec as AudioCodec);
        audioSink = new EncodedPacketSink(audioTrack);
        output.addAudioTrack(audioSource);
      }
    }

    try {
      await output.start();

      const writeVideo = async () => {
        for (let index = 0; index < result.samples.length; index += 1) {
          const sample = result.samples[index];
          if (!sample) continue;

          const durationMicroseconds = sample.duration ?? Math.round(1_000_000 / result.fps);
          const packet = new EncodedPacket(
            sample.data,
            sample.type,
            sample.timestamp / 1_000_000,
            durationMicroseconds / 1_000_000,
            index,
          );

          const decoderConfig = sample.decoderConfig;
          await videoSource.add(
            packet,
            decoderConfig
              ? {
                  decoderConfig: {
                    ...decoderConfig,
                    codedWidth: decoderConfig.codedWidth ?? result.width,
                    codedHeight: decoderConfig.codedHeight ?? result.height,
                  },
                }
              : undefined,
          );
        }
        videoSource.close();
      };

      const writeAudio = async () => {
        if (!audioSource || !audioSink || !audioDecoderConfig) return;
        let first = true;
        for await (const packet of audioSink.packets()) {
          await audioSource.add(packet, first ? { decoderConfig: audioDecoderConfig } : undefined);
          first = false;
        }
        audioSource.close();
      };

      await Promise.all([writeVideo(), writeAudio()]);
      await output.finalize();

      if (!target.buffer) {
        throw new AppError("EXPORT_FAILED", "The video muxer produced an empty output buffer.");
      }

      return new Blob([target.buffer], { type: mimeType(this.container) });
    } catch (cause) {
      try {
        await output.cancel();
      } catch {
        // Preserve the original muxing failure.
      }
      if (cause instanceof AppError) throw cause;
      throw new AppError("EXPORT_FAILED", `Failed to mux ${this.container.toUpperCase()} video.`, cause);
    }
  }
}
